/**
 * Helpers for the HTML note bodies introduced in spec-58.
 *
 * Why these live next to `features/postinvest/dossier/htmlToText.ts` instead of
 * replacing it: that one decorates list items with a bullet so HTML reads well
 * inside a `whitespace-pre-wrap` container. These helpers are for matching and
 * emptiness checks, where such decoration is noise — a `•` would end up in the
 * text an @mention is searched in.
 */

const LEADING_TAG = /^\s*</

/** True when the value starts with a tag, so it should be rendered as HTML. */
export function looksLikeHtml(value: string): boolean {
  return LEADING_TAG.test(value)
}

/**
 * The readable text inside a rich-text body: block ends become newlines,
 * entities are decoded and tags disappear without leaving markers behind.
 *
 * Parsing happens in an inert DOMParser document, so untrusted HTML never
 * touches the live DOM.
 */
export function richTextToPlain(value: string): string {
  if (!value || !/[<&]/.test(value)) return value
  const prepared = value
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(p|li|ul|ol|h[1-6]|blockquote)>/gi, '\n')
  const doc = new DOMParser().parseFromString(prepared, 'text/html')
  return (doc.body.textContent ?? '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim()
}

/**
 * True when the body holds no text. A fresh TipTap editor serialises to
 * `<p></p>`, which the backend rejects with 422, so the composer uses this to
 * keep its save button disabled instead of letting that request go out.
 */
export function isRichTextEmpty(value: string): boolean {
  return richTextToPlain(value).replace(/\u00a0/g, ' ').trim() === ''
}

const TRAILING_EMPTY_BLOCK = /(?:<p>(?:\s|<br\s*\/?>|&nbsp;)*<\/p>)+$/i

/**
 * Strips the empty paragraph that TipTap's TrailingNode appends after a
 * heading or list. Without this every note ending in a bullet would be stored
 * with a stray `<p></p>` and show a blank line under the last bullet.
 */
export function trimRichText(value: string): string {
  return value.trim().replace(TRAILING_EMPTY_BLOCK, '')
}

/**
 * Plain text as rich text inside the whitelist: one paragraph, `&`, `<` and `>` escaped, a line
 * break as `<br>`. The same as `text_to_html` in `backend/app/preinvest/rich_text.py`, which the
 * backend uses when an old frontend writes `notes`. For text that goes into the HTML editor: it
 * would parse "Jan <jg@x.nl>" as a tag and take the line breaks of a textarea for blanks.
 */
export function plainTextToHtml(text: string): string {
  const escaped = text.trim().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return `<p>${escaped.replace(/\r\n?|\n/g, '<br>')}</p>`
}
